import React, { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { Badge, Button, ButtonGroup, Card, CardHeader, Col, Input, Row, Table } from 'reactstrap'
import { ToastContainer } from 'react-toastify'
import Header from '../../components/Headers/Header'
import { api } from '../../utils'
import { notify } from '../../utils/notify'
import { getLLMVisibilityClientName } from './clienteDisplay'

const itemPerPage = 10

const LLMVisibilityScheduledListingV2 = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('schedules')
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [schedules, setSchedules] = useState([])
  const [executions, setExecutions] = useState([])
  const [schedulePages, setSchedulePages] = useState(0)
  const [executionPages, setExecutionPages] = useState(0)
  const [schedulePage, setSchedulePage] = useState(1)
  const [executionPage, setExecutionPage] = useState(1)
  const [rerunLoadingId, setRerunLoadingId] = useState(null)
  const [scheduleSearch, setScheduleSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [modeFilter, setModeFilter] = useState('all')

  const formatDate = (value) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString()
  }

  const getNextRunLabel = (startDate, scheduleType = 'M', interval = 1) => {
    if (!startDate) return '-'
    const anchorDate = new Date(startDate)
    if (Number.isNaN(anchorDate.getTime())) return '-'

    const normalizedInterval = Math.max(1, Number(interval) || 1)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    if (scheduleType === 'W') {
      const firstRun = new Date(
        anchorDate.getFullYear(),
        anchorDate.getMonth(),
        anchorDate.getDate()
      )
      const anchorWeekday = anchorDate.getDay()
      const nextRun = new Date(today)
      const daysUntilAnchor = (anchorWeekday - today.getDay() + 7) % 7
      nextRun.setDate(today.getDate() + daysUntilAnchor)
      if (nextRun < firstRun) {
        return firstRun.toLocaleDateString()
      }
      const weeksDiff = Math.floor((nextRun - firstRun) / (1000 * 60 * 60 * 24 * 7))
      const remainder = weeksDiff % normalizedInterval
      if (remainder !== 0) {
        nextRun.setDate(nextRun.getDate() + (normalizedInterval - remainder) * 7)
      }
      return nextRun.toLocaleDateString()
    }

    const anchorDay = anchorDate.getDate()
    const getRunDateForMonth = (year, month) => {
      const lastDay = new Date(year, month + 1, 0).getDate()
      const runDay = Math.min(anchorDay, lastDay)
      return new Date(year, month, runDay)
    }

    const startOfAnchorMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1)
    let monthDiff =
      (today.getFullYear() - startOfAnchorMonth.getFullYear()) * 12 +
      (today.getMonth() - startOfAnchorMonth.getMonth())
    if (monthDiff < 0) {
      monthDiff = 0
    }
    const remainder = monthDiff % normalizedInterval
    if (remainder !== 0) {
      monthDiff += normalizedInterval - remainder
    }
    let runMonth = new Date(
      startOfAnchorMonth.getFullYear(),
      startOfAnchorMonth.getMonth() + monthDiff,
      1
    )
    let nextRun = getRunDateForMonth(runMonth.getFullYear(), runMonth.getMonth())
    if (nextRun < today) {
      runMonth = new Date(runMonth.getFullYear(), runMonth.getMonth() + normalizedInterval, 1)
      nextRun = getRunDateForMonth(runMonth.getFullYear(), runMonth.getMonth())
    }
    return nextRun.toLocaleDateString()
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'DONE':
      case 'EXPORTED':
      case 'EXPORTED_QUESTIONS':
        return 'success'
      case 'RUNNING':
      case 'RETRIEVING_ANSWERS':
      case 'RETRIEVING_QUESTIONS':
        return 'warning'
      case 'ERROR':
        return 'danger'
      default:
        return 'primary'
    }
  }

  const getModeState = (item) => {
    if (!item.questions_only) {
      return { label: 'Monthly Answers Active', color: 'success' }
    }
    if (item.questions_generated) {
      return { label: 'Questions Ready', color: 'primary' }
    }
    return { label: 'Questions Pending', color: 'warning' }
  }

  const fetchSchedules = async (page = 1) => {
    setLoading(true)
    try {
      const res = await api.get('llm-visibility-schedules/', {
        params: { page_size: itemPerPage, page },
      })
      const results = res.data.results || res.data
      setSchedules(results)
      setSchedulePages(Math.ceil((res.data.count || results.length) / itemPerPage))
      setSchedulePage(page)
    } catch (err) {
      notify('Failed to load schedules.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const fetchExecutions = async (schedule, page = 1) => {
    if (!schedule) return
    setLoading(true)
    try {
      setSelectedSchedule(schedule)
      const res = await api.get('llm-visibility-scheduled-executions/', {
        params: { schedule_id: schedule.id, page_size: itemPerPage, page },
      })
      const results = res.data.results || res.data
      setExecutions(results)
      setExecutionPages(Math.ceil((res.data.count || results.length) / itemPerPage))
      setExecutionPage(page)
      setActiveTab('executions')
    } catch (err) {
      notify('Failed to load executions.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handlePageClick = async (data) => {
    const currentPage = data.selected + 1
    if (activeTab === 'schedules') {
      await fetchSchedules(currentPage)
      return
    }
    if (selectedSchedule) {
      await fetchExecutions(selectedSchedule, currentPage)
    }
  }

  const rerunExecution = async (executionId) => {
    setRerunLoadingId(executionId)
    try {
      await api.post(`llm-visibility-scheduled-executions/${executionId}/rerun/`)
      notify('Execution rerun started.', 'success')
      if (selectedSchedule) {
        await fetchExecutions(selectedSchedule, executionPage)
      }
    } catch (err) {
      notify(err?.response?.data?.error || 'Failed to rerun execution.', 'error')
    } finally {
      setRerunLoadingId(null)
    }
  }

  const filteredSchedules = schedules.filter((item) => {
    const normalizedSearch = scheduleSearch.trim().toLowerCase()
    const matchesSearch =
      normalizedSearch === '' ||
      (item.name || '').toLowerCase().includes(normalizedSearch) ||
      getLLMVisibilityClientName(item).toLowerCase().includes(normalizedSearch)
    const matchesActive =
      activeFilter === 'all' ||
      (activeFilter === 'active' && item.active) ||
      (activeFilter === 'inactive' && !item.active)
    const matchesMode =
      modeFilter === 'all' ||
      (modeFilter === 'questions' && item.questions_only) ||
      (modeFilter === 'answers' && !item.questions_only)
    return matchesSearch && matchesActive && matchesMode
  })

  useEffect(() => {
    fetchSchedules(1).catch(() => setLoading(false))
  }, [])

  return (
    <Header
      iconClass="far fa-calendar-alt text-white"
      title="Relevia Setup"
      description="Configura con wizard guidato Relevia"
    >
      {loading && (
        <div className="text-center" role="status" aria-live="polite">
          <Spinner animation="grow" />
          <span className="sr-only">
            {activeTab === 'schedules' ? 'Caricamento schedule...' : 'Caricamento esecuzioni...'}
          </span>
        </div>
      )}
      {!loading && (
        <Row style={{ marginBottom: '100px' }}>
          <div className="col pb-5">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center mb-3">
                  <Col xs="9">
                    <h1 className="mb-0">Relevia Setup</h1>
                  </Col>
                  <Col className="text-right" xs="3">
                    <Button
                      color="info"
                      onClick={() => history.push('/add-relevia-configuration/')}
                      size="md"
                    >
                      + Aggiungi
                    </Button>
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <Col md="6">
                    <ButtonGroup>
                      <Button
                        color={activeTab === 'schedules' ? 'primary' : 'secondary'}
                        onClick={() => setActiveTab('schedules')}
                      >
                        Schedules
                      </Button>
                      <Button
                        color={activeTab === 'executions' ? 'primary' : 'secondary'}
                        onClick={() => setActiveTab('executions')}
                        disabled={!selectedSchedule}
                      >
                        Executions
                      </Button>
                    </ButtonGroup>
                  </Col>
                  {activeTab === 'executions' && selectedSchedule && (
                    <Col md="6" className="text-right">
                      <span className="text-muted">Schedule: {selectedSchedule.name}</span>
                    </Col>
                  )}
                </Row>
                {activeTab === 'schedules' && (
                  <Row className="mt-3">
                    <Col md="4">
                      <label htmlFor="scheduled-search" className="form-control-label">
                        Cerca schedule
                      </label>
                      <Input
                        id="scheduled-search"
                        type="text"
                        placeholder="Search schedule or cliente"
                        value={scheduleSearch}
                        onChange={(e) => setScheduleSearch(e.target.value)}
                      />
                    </Col>
                    <Col md="4">
                      <label htmlFor="scheduled-active-filter" className="form-control-label">
                        Stato attivazione
                      </label>
                      <Input
                        id="scheduled-active-filter"
                        type="select"
                        value={activeFilter}
                        onChange={(e) => setActiveFilter(e.target.value)}
                      >
                        <option value="all">All Activity</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Input>
                    </Col>
                    <Col md="4">
                      <label htmlFor="scheduled-mode-filter" className="form-control-label">
                        Modalità
                      </label>
                      <Input
                        id="scheduled-mode-filter"
                        type="select"
                        value={modeFilter}
                        onChange={(e) => setModeFilter(e.target.value)}
                      >
                        <option value="all">All Modes</option>
                        <option value="questions">Questions Only</option>
                        <option value="answers">Scheduled Answers</option>
                      </Input>
                    </Col>
                  </Row>
                )}
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                {activeTab === 'schedules' ? (
                  <>
                    <thead className="thead-light">
                      <tr>
                        <th>Name</th>
                        <th>Cliente</th>
                        <th>Start Date</th>
                        <th>Type</th>
                        <th>Interval</th>
                        <th>Next Run</th>
                        <th>Mode State</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSchedules.length === 0 && (
                        <tr>
                          <td colSpan="9" className="text-center text-muted">
                            No schedules found for current filters.
                          </td>
                        </tr>
                      )}
                      {filteredSchedules.map((item) => (
                        <tr key={item.id}>
                          <td>{item.name}</td>
                          <td>{getLLMVisibilityClientName(item) || '-'}</td>
                          <td>{item.start_date || '-'}</td>
                          <td>{item.schedule_type === 'W' ? 'Weekly' : 'Monthly'}</td>
                          <td>{item.interval || 1}</td>
                          <td>
                            {item.active
                              ? getNextRunLabel(item.start_date, item.schedule_type, item.interval)
                              : '-'}
                          </td>
                          <td>
                            <Badge color={getModeState(item).color} pill className="text-white">
                              {getModeState(item).label}
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              color={item.active ? 'success' : 'secondary'}
                              pill
                              className="text-white"
                            >
                              {item.active ? 'ACTIVE' : 'PAUSED'}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              className="mr-2"
                              onClick={() => fetchExecutions(item, 1)}
                            >
                              Executions
                            </Button>
                            <Button
                              color="default"
                              size="sm"
                              className="mr-2"
                              onClick={() =>
                                history.push(`/add-relevia-configuration/${item.id}`)
                              }
                            >
                              Edit
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                ) : (
                  <>
                    <thead className="thead-light">
                      <tr>
                        <th>Period</th>
                        <th>Execution Status</th>
                        <th>LLM Visibility Status</th>
                        <th>Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!selectedSchedule && (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            Select a schedule and open its executions to view this section.
                          </td>
                        </tr>
                      )}
                      {selectedSchedule && executions.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            No executions found.
                          </td>
                        </tr>
                      )}
                      {executions.map((item) => (
                        <tr key={item.id}>
                          <td>{item.period}</td>
                          <td>
                            <Badge color={getStatusBadge(item.status)} pill className="text-white">
                              {item.status}
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              color={getStatusBadge(item.llm_visibility_status)}
                              pill
                              className="text-white"
                            >
                              {item.llm_visibility_status}
                            </Badge>
                          </td>
                          <td>{formatDate(item.modified || item.created)}</td>
                          <td>
                            {item.llm_visibility_status !== 'EXPORTED' && (
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => rerunExecution(item.id)}
                                disabled={item.status === 'RUNNING' || rerunLoadingId === item.id}
                              >
                                {rerunLoadingId === item.id ? 'Rerunning...' : 'Rerun'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}
              </Table>
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={activeTab === 'schedules' ? schedulePages : executionPages}
                forcePage={(activeTab === 'schedules' ? schedulePage : executionPage) - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            </Card>
          </div>
        </Row>
      )}
      <ToastContainer />
    </Header>
  )
}

export default LLMVisibilityScheduledListingV2
